import { useState } from 'react';
import { NotificationsOutlined, VideocamOutlined } from '@mui/icons-material';
import { AiOutlineCalculator } from 'react-icons/ai';
import { MdOutlineAssignment } from 'react-icons/md';
import useSurvey from 'store/survey/useSurvey';
import usePatient from 'store/patient/usePatient';
import useStudent from 'store/student/useStudent';
import useUser from 'store/user/useUser';
import useNotification from 'hooks/useNotification';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

const Header = () => {
  const [calcAnchorEl, setCalcAnchorEl] = useState<null | HTMLElement>(null);

  const { student_uuid } = useStudent();
  const { student_grade } = useUser();
  const { patientInfo } = usePatient();
  /** 1: 학생만 숨김. 교수/조교(2·5), 학교관리자 등 그 외 숫자 등급은 표시 */
  const showProfessorScreen =
    typeof student_grade === 'number' && student_grade !== 1;
  const { onRequired } = useNotification();
  const { onUpdateSurveyType } = useSurvey();

  const calcOpen = Boolean(calcAnchorEl);

  const onClickListItem = (label: string) => {
    if (label === '교수자 전용 화면') {
      const url = `${window.location.origin}/#/professorAuth`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (label === '공지사항') {
      window.open(
        'https://www.smartnurse.co.kr/notice',
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    if (!student_uuid) return onRequired('REQUIRED.STUDENT');
    if (!patientInfo) return onRequired('REQUIRED.PATIENT');
    onUpdateSurveyType(label);
  };

  const calcItems = ['약물계산기', 'Medcalc', '의학계산기'];

  const handleCalcButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!student_uuid) return onRequired('REQUIRED.STUDENT');
    setCalcAnchorEl(e.currentTarget);
  };

  const handleCalcClose = () => setCalcAnchorEl(null);

  const externalUrlByLabel: Record<string, string | undefined> = {
    Medcalc: 'http://medcalc.ajoumc.or.kr/',
    의학계산기: 'https://medcalc.co.kr/',
  };

  const handleCalcItemClick = (subLabel: string) => {
    const extUrl = externalUrlByLabel[subLabel];

    if (extUrl) {
      // 외부 링크는 환자 선택 체크 없이 바로 새 탭 오픈
      window.open(extUrl, '_blank', 'noopener,noreferrer');
      handleCalcClose();
      return;
    }

    // 내부 항목은 기존 흐름 유지
    if (!patientInfo) {
      onRequired('REQUIRED.PATIENT');
      return;
    }
    onUpdateSurveyType(subLabel);
    handleCalcClose();
  };

  const menu = [
    ...(showProfessorScreen
      ? [
          {
            icon: <PeopleAltOutlinedIcon />,
            label: '교수자 전용 화면',
            width: '120px',
          },
        ]
      : []),
    {
      icon: <AiOutlineCalculator />,
      label: 'Calc & Scale',
      onClick: handleCalcButtonClick,
      width: '120px',
    },
    {
      icon: <MdOutlineAssignment />,
      label: '과제 제출',
      width: '70px',
    },
    {
      icon: <VideocamOutlined />,
      label: '핵심간호술기영상 저장',
      width: '160px',
    },
    {
      icon: <NotificationsOutlined />,
      label: '공지사항',
      width: '120px',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        direction: 'row',
        width: '700px',
        alignItems: 'center',
      }}
    >
      {menu.map(({ icon, label, onClick, width }) => {
        const handleClick =
          label === 'Calc & Scale' ? onClick : () => onClickListItem(label);

        return (
          <ListItem
            key={label}
            disablePadding
            ref={null}
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            <ListItemButton
              onClick={handleClick as any}
              sx={{
                display: 'block',
                width:
                  width ??
                  (label === '핵심간호술기영상 저장' ? '150px' : '100px'),
                height: '40px',
                p: 0,
                alignItems: 'center',
                borderRadius: '8px',
                m: '1px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListItemIcon
                  className="HeadName"
                  sx={{
                    my: '6px',
                    justifyContent: 'center',
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: '12px',
                    textAlign: 'center',
                    width: '100px',
                  }}
                  primary={label}
                />
              </Box>
            </ListItemButton>
          </ListItem>
        );
      })}

      {/* Calc & Scale 드롭다운 */}
      <Menu
        anchorEl={calcAnchorEl}
        open={calcOpen}
        onClose={handleCalcClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        keepMounted
        disableScrollLock
      >
        {calcItems.map((item, idx) => (
          <Box key={item}>
            {idx > 0 && <Divider sx={{ my: 0 }} />}
            <MenuItem
              disableGutters
              sx={{
                px: '12px',
                width: '150px',
                py: '10px',
                minHeight: 'unset',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                '&:hover': { backgroundColor: '#ddd' },
              }}
              onClick={() => handleCalcItemClick(item)}
            >
              {item}
            </MenuItem>
          </Box>
        ))}
      </Menu>
    </Box>
  );
};

export default Header;
